import React, { useState, useEffect, useRef } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { NAV_LINKS } from '../constants';
import type { NavLink } from '../types';
import { useTheme } from '../hooks/useTheme';
import { Sun, Moon, Menu, X, Phone, ChevronDown, ChevronRight, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { trackPlausibleEvent } from '../utils/tracking';
import { BrandMark } from './BrandMark';
import { StatusWidget } from './gaca/StatusWidget';

const ThemeToggle: React.FC = () => {
    const { theme, toggleTheme } = useTheme();
    return (
        <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
            aria-label="Zmień motyw"
        >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
        </button>
    );
};

const DesktopDropdown: React.FC<{ link: NavLink; }> = ({ link }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeSubMenuKey, setActiveSubMenuKey] = useState<string | null>(null);
    const timeoutRef = useRef<number | null>(null);
    const dropdownId = `desktop-dropdown-${link.name.replace(/\s+/g, '-')}`;

    const handleOpen = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsOpen(true);
    };

    const handleClose = () => {
        timeoutRef.current = window.setTimeout(() => {
            setIsOpen(false);
            setActiveSubMenuKey(null);
        }, 200);
    };

    const handleContentEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    return (
        <div className="relative" onMouseEnter={handleOpen} onMouseLeave={handleClose}>
            <button
              className="flex items-center px-3 py-2 rounded-xl text-sm font-medium text-text-primary-light dark:text-text-primary-dark transition-all duration-200 hover:bg-white/10 dark:hover:bg-white/5"
              aria-haspopup="true"
              aria-expanded={isOpen}
              aria-controls={dropdownId}
            >
                <span>{link.name}</span>
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div
                    id={dropdownId}
                    className="absolute top-full left-0 mt-2 w-72 bg-white/80 dark:bg-content-dark/90 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl shadow-2xl py-2 animate-scale-in origin-top"
                    onMouseEnter={handleContentEnter}
                >
                    {link.subLinks?.map((subLink) =>
                        subLink.subLinks ? (
                            <div
                                key={subLink.name}
                                className="relative"
                                onMouseEnter={() => setActiveSubMenuKey(subLink.name)}
                            >
                                <div className="mx-2 px-4 py-3 flex justify-between items-center text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-white/50 dark:hover:bg-white/10 rounded-xl cursor-default transition-colors">
                                    <span className="flex items-center gap-3">
                                        <span className="w-8 h-8 rounded-lg bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center">
                                            {subLink.icon}
                                        </span>
                                        <span className="font-medium">{subLink.name}</span>
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
                                </div>
                                {activeSubMenuKey === subLink.name && (
                                    <div className="absolute top-0 left-full -mt-2 ml-2 w-72 bg-white/80 dark:bg-content-dark/90 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl shadow-2xl py-2 animate-scale-in origin-left">
                                        {subLink.subLinks.map((productLink) => (
                                            <ReactRouterDOM.Link
                                                key={productLink.name}
                                                to={productLink.path}
                                                onClick={() => setIsOpen(false)}
                                                className="mx-2 flex items-center gap-3 px-4 py-3 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-white/50 dark:hover:bg-white/10 rounded-xl transition-colors"
                                            >
                                                <span className="w-8 h-8 rounded-lg bg-secondary-light/10 dark:bg-secondary-dark/10 flex items-center justify-center">
                                                    {productLink.icon}
                                                </span>
                                                <span className="font-medium">{productLink.name}</span>
                                            </ReactRouterDOM.Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <ReactRouterDOM.Link
                                key={subLink.name}
                                to={subLink.path}
                                onClick={() => setIsOpen(false)}
                                className="mx-2 block px-4 py-3 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-white/50 dark:hover:bg-white/10 rounded-xl transition-colors"
                            >
                                {subLink.name}
                            </ReactRouterDOM.Link>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuStack, setMenuStack] = useState<NavLink[]>(() => [{ name: 'Menu Główne', path: '#', subLinks: NAV_LINKS }]);

  const location = ReactRouterDOM.useLocation();

  useEffect(() => {
    if (!isMenuOpen) {
      setTimeout(() => {
        setMenuStack([{ name: 'Menu Główne', path: '#', subLinks: NAV_LINKS }]);
      }, 300);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const activeMenu = menuStack[menuStack.length - 1];
  const canGoBack = menuStack.length > 1;

  const handleForward = (link: NavLink) => {
    setMenuStack(prev => [...prev, link]);
  };

  const handleBack = () => {
    setMenuStack(prev => prev.slice(0, -1));
  };

  const renderNavLink = (link: NavLink, isMobile = false) => {
    const commonDesktopClasses = `flex items-center px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200`;
    const activeDesktopClasses = 'bg-white/20 dark:bg-white/10 text-primary-light dark:text-primary-dark';
    const inactiveDesktopClasses = 'text-text-primary-light dark:text-text-primary-dark hover:bg-white/10 dark:hover:bg-white/5';
    const mobileClasses = "flex items-center gap-3 px-4 py-4 rounded-xl text-base font-medium hover:bg-white/10 dark:hover:bg-white/5 transition-colors";

    if (link.external) {
        return (
            <a
                key={link.name}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-id={`menu_${isMobile ? 'mobile' : 'desktop'}_wspolpraca`}
                className={isMobile ? mobileClasses : `${commonDesktopClasses} ${inactiveDesktopClasses}`}
                onClick={() => {
                  trackPlausibleEvent('cta_click', { placement: 'menu_wspolpraca' });
                  if (isMobile) setIsMenuOpen(false);
                }}
            >
                {isMobile && link.icon}
                <span>{link.name}</span>
                {!isMobile && <ArrowUpRight className="w-4 h-4 ml-1 opacity-70" />}
            </a>
        );
    }
    return (
        <ReactRouterDOM.NavLink
            key={link.name}
            to={link.path}
            onClick={() => isMobile && setIsMenuOpen(false)}
            className={isMobile ? mobileClasses : ({ isActive }) => `${commonDesktopClasses} ${isActive ? activeDesktopClasses : inactiveDesktopClasses}`}
        >
            {isMobile && link.icon}
            <span>{link.name}</span>
        </ReactRouterDOM.NavLink>
    );
};

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-500
          ${isScrolled
            ? 'bg-white/70 dark:bg-bkg-dark/80 backdrop-blur-xl border-b border-white/20 dark:border-white/10 shadow-lg'
            : 'bg-transparent'
          }
        `}
      >
        {/* Gradient border bottom when scrolled */}
        {isScrolled && (
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-light/50 dark:via-primary-dark/50 to-transparent" />
        )}

        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <ReactRouterDOM.NavLink
              to="/"
              className="flex items-center gap-2 text-xl font-bold text-primary-light dark:text-primary-dark hover:opacity-80 transition-opacity"
            >
              {/* Sygnet marki. Do 12.08.2026 stała tu ikona błyskawicy z lucide-react w gradientowym
                  kwadracie — placeholder bez związku ze znakiem BBK. */}
              <BrandMark className="w-8 h-8 shrink-0" />
              <span className="hidden sm:inline">BiznesBezKlikania</span>
              <span className="sm:hidden">BBK</span>
            </ReactRouterDOM.NavLink>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" aria-label="Główna nawigacja">
              {NAV_LINKS.map(link =>
                link.subLinks ? (
                  <DesktopDropdown key={link.name} link={link} />
                ) : (
                  renderNavLink(link)
                )
              )}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              <StatusWidget variant="badge" />
              <ThemeToggle />
              <a
                href="tel:+48791581239"
                className="
                  flex items-center space-x-2 px-5 py-2.5
                  text-sm font-semibold text-white
                  bg-gradient-to-r from-primary-light to-secondary-light
                  dark:from-primary-dark dark:to-secondary-dark
                  rounded-xl
                  hover:shadow-lg hover:scale-[1.02]
                  transition-all duration-300
                "
              >
                <Phone className="w-4 h-4" />
                <span>Zadzwoń</span>
              </a>
            </div>

            {/* Mobile Actions */}
            <div className="lg:hidden flex items-center gap-2">
              <StatusWidget variant="minimal" />
              <ThemeToggle />
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-xl hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
                aria-label={isMenuOpen ? 'Zamknij menu' : 'Otwórz menu'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Slide in from right */}
      <div
        className={`
          lg:hidden fixed inset-0 z-40
          transition-all duration-300
          ${isMenuOpen ? 'visible' : 'invisible'}
        `}
      >
        {/* Backdrop */}
        <div
          className={`
            absolute inset-0 bg-black/50 backdrop-blur-sm
            transition-opacity duration-300
            ${isMenuOpen ? 'opacity-100' : 'opacity-0'}
          `}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          id="mobile-menu"
          className={`
            absolute top-0 right-0 bottom-0 w-full max-w-sm
            bg-white/90 dark:bg-bkg-dark/95 backdrop-blur-xl
            border-l border-white/20 dark:border-white/10
            shadow-2xl
            transition-transform duration-300 ease-out
            ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-border-light/50 dark:border-border-dark/50">
              {canGoBack ? (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-text-primary-light dark:text-text-primary-dark"
                  aria-label="Wróć do poprzedniego menu"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="font-medium">Wstecz</span>
                </button>
              ) : (
                <h3 className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark">
                  Menu
                </h3>
              )}
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-xl hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
                aria-label="Zamknij menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="flex-grow overflow-y-auto p-4 space-y-2" aria-label="Mobilna nawigacja">
              {activeMenu.subLinks?.map(link => (
                link.subLinks ? (
                  <button
                    key={link.name}
                    onClick={() => handleForward(link)}
                    className="
                      w-full flex justify-between items-center
                      px-4 py-4 rounded-xl
                      text-base font-medium text-left
                      hover:bg-white/10 dark:hover:bg-white/5
                      transition-colors
                    "
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-xl bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center">
                        {link.icon}
                      </span>
                      <span>{link.name}</span>
                    </span>
                    <ChevronRight className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
                  </button>
                ) : (
                  <div key={link.name} className="flex items-center">
                    {link.icon && (
                      <span className="w-10 h-10 rounded-xl bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mr-3">
                        {link.icon}
                      </span>
                    )}
                    {renderNavLink(link, true)}
                  </div>
                )
              ))}
            </nav>

            {/* Mobile Menu Footer */}
            <div className="p-4 border-t border-border-light/50 dark:border-border-dark/50 space-y-3">
              <StatusWidget variant="detailed" className="mb-3" />
              <a
                href="tel:+48791581239"
                className="
                  flex items-center justify-center space-x-2
                  px-4 py-3
                  text-sm font-semibold text-white
                  bg-gradient-to-r from-primary-light to-secondary-light
                  dark:from-primary-dark dark:to-secondary-dark
                  rounded-xl
                  hover:opacity-90 transition-opacity
                  w-full
                "
              >
                <Phone className="w-4 h-4" />
                <span>Zadzwoń i umów konsultację</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out forwards;
        }
      `}</style>
    </>
  );
};
